import type {
  AppValidationReport,
  PerBlockResult,
  ValidatorRowEntry,
} from './types'
import { deriveStatus } from './types'
import { humanizeRuleId } from './humanize-rule-id'

const GATE_ORDER: Array<'structural' | 'binding'> = ['structural', 'binding']

interface RowAccumulator {
  gate: 'structural' | 'binding'
  ruleId: string
  passCount: number
  failCount: number
  warnCount: number
}

export function aggregateReport(
  perBlock: PerBlockResult[],
  durationMs: number,
): AppValidationReport {
  let totalRules = 0
  let violations = 0
  let warnings = 0
  const rowMap = new Map<string, RowAccumulator>()

  for (const block of perBlock) {
    for (const gate of GATE_ORDER) {
      const phase = block.phases[gate]
      totalRules += phase.rulesEvaluated
      for (const violation of phase.violations) {
        const key = `${gate}/${violation.shape}`
        const accum = rowMap.get(key) ?? {
          gate,
          ruleId: violation.shape,
          passCount: 0,
          failCount: 0,
          warnCount: 0,
        }
        if (violation.severity === 'violation') {
          accum.failCount += 1
          violations += 1
        } else if (violation.severity === 'warning') {
          accum.warnCount += 1
          warnings += 1
        } else {
          accum.passCount += 1
        }
        rowMap.set(key, accum)
      }
    }
  }

  const totalViolations = violations + warnings
  const pass = Math.max(0, totalRules - totalViolations)

  const rows: ValidatorRowEntry[] = [...rowMap.values()]
    .map((r) => ({
      gate: r.gate,
      ruleId: r.ruleId,
      ruleLabel: humanizeRuleId(r.ruleId),
      passCount: r.passCount,
      failCount: r.failCount,
      warnCount: r.warnCount,
      status: r.failCount > 0 ? ('destructive' as const) : r.warnCount > 0 ? ('warning' as const) : ('success' as const),
    }))
    .sort((a, b) => {
      const gateCmp = GATE_ORDER.indexOf(a.gate) - GATE_ORDER.indexOf(b.gate)
      if (gateCmp !== 0) return gateCmp
      return a.ruleId.localeCompare(b.ruleId)
    })

  return {
    status: deriveStatus({ violations, warnings }),
    totalRules,
    totalViolations,
    pass,
    violations,
    warnings,
    blocks: perBlock,
    rows,
    durationMs,
    fromLocal: true,
  }
}
